import { useState, useMemo } from 'react';
import styled from 'styled-components';

import { pxToRem } from 'utils/convert';
import { TypeaheadSelect } from 'ui/selects';
import { SelectWrapper } from 'ui/selects/Selects.styles';
import { spacing } from 'ui/theme';

import {
  getFilterSegmentSelectInputTriggerText,
  createFilterSelectOption,
} from 'modules/journey/components/FilterInputsList/utils';
import { FilterSegmentSelectInputOption } from '../FilterInputsList.types';
import { FilterSegmentButton } from './FilterSegmentButton';
import { FilterSegmentErrorButton } from './FilterSegmentErrorButton';

const content = {
  propertySelectPrompt: 'Select a property',
};

type FilterSegmentSelectInputProps<OptionType> = {
  options: OptionType[];
  selectedOptionIds: string[];
  onSelect: (selectedOptions: OptionType[]) => void;
  id: string;
  placeholder: string;
  isDisabled?: boolean;
  isFilterable?: boolean;
  hasError?: boolean;
};

const StyledSelectWrapper = styled(SelectWrapper)`
  border: none;
  margin-right: ${spacing.half};

  &:last-of-type {
    margin-right: 0;
  }
`;

const StyledTypeaheadSelect = styled(TypeaheadSelect)`
  width: ${pxToRem(200)};
`;

const StyledTriggerText = styled.div`
  padding: ${spacing.single} 0px;
`;
const customSelectionToOption = (customSelection: string): FilterSegmentSelectInputOption =>
  createFilterSelectOption(customSelection, customSelection, {
    fieldType: 'string',
    dataType: 'customEvent',
  });

export const FILTER_SEGMENT_BUTTON_TEST_ID = 'filter-segment-button-test-id';

export const FilterSegmentTypeaheadSelect = <OptionType extends FilterSegmentSelectInputOption>({
  options,
  onSelect,
  selectedOptionIds = [],
  id,
  placeholder,
  isDisabled,
  hasError = false,
}: FilterSegmentSelectInputProps<OptionType>) => {
  const [showTypeahead, setShowTypeahead] = useState(false);
  const matchedSelectedOptions = useMemo(
    () => options.filter((option) => selectedOptionIds.includes(option.id)),
    [options, selectedOptionIds],
  );
  const selectedOptions = useMemo(() => {
    const hasMatchedSelectedOptions = matchedSelectedOptions.length > 0;
    if (hasMatchedSelectedOptions) return matchedSelectedOptions;
    const hasSelectedOptionIds = selectedOptionIds.length > 0 && selectedOptionIds[0] !== null;
    return hasSelectedOptionIds ? selectedOptionIds.map(customSelectionToOption) : [];
  }, [matchedSelectedOptions, selectedOptionIds]);

  const triggerText = useMemo(
    () => getFilterSegmentSelectInputTriggerText(selectedOptions, placeholder),
    [selectedOptions, placeholder],
  );

  const ButtonComponent = hasError ? FilterSegmentErrorButton : FilterSegmentButton;

  return (
    <>
      {!showTypeahead && (
        <ButtonComponent
          onClick={() => setShowTypeahead(true)}
          data-test={FILTER_SEGMENT_BUTTON_TEST_ID}
          isDisabled={isDisabled}
        >
          <StyledTriggerText>{triggerText}</StyledTriggerText>
        </ButtonComponent>
      )}
      {showTypeahead && (
        <StyledTypeaheadSelect
          id={id}
          isError={hasError}
          defaultValues={selectedOptionIds}
          onSelect={(selectedIds) => {
            const selectedId = selectedIds[0].toString();
            const matchedOption = options.filter((option) => selectedId === option.id);
            const hasMatchedOption = matchedOption.length > 0;
            const selection = hasMatchedOption
              ? matchedOption[0]
              : customSelectionToOption(selectedId);

            onSelect([selection as OptionType]); // give them the option to select something not available as an option
            setShowTypeahead(false);
          }}
          options={options}
          SelectWrapper={StyledSelectWrapper}
          placeholder={content.propertySelectPrompt}
          isDisabled={isDisabled}
          autoFocus
          isVirtualized={true}
        />
      )}
    </>
  );
};
